import { FC, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { Button } from 'primereact/button';
import ResetDiagnosticDialog from './ResetDiagnosticDialog';
import { formatActiveTime, getStudentName, StudentReport } from './closerLookHelper';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  student: StudentReport;
  variant: 'too-long' | 'too-fast';
  borderColorClass: string;
  demonstratedSkills?: number | null | undefined;
  isDismissed?: boolean;
  /**
   * Keeps an empty slot the width of the "too-fast" row actions, so both variants line up
   * when they are shown as sibling tabs. Only applies to the "too-long" variant.
   */
  reserveActionsColumn?: boolean;
  onDismiss?: (report: StudentReport) => void;
  onConfirmDialogToggle?: (open: boolean) => void;
}

const CloserLookStudentRow: FC<Props> = ({
  student,
  variant,
  borderColorClass,
  demonstratedSkills,
  isDismissed = false,
  reserveActionsColumn = true,
  onDismiss,
  onConfirmDialogToggle,
}) => {
  const [showReset, setShowReset] = useState(false);

  const toggleReset = (open: boolean) => {
    setShowReset(open);
    onConfirmDialogToggle?.(open);
  };

  const studentName = getStudentName(student.student);
  const questionsAnswered = student.questionsAnswered ?? 0;
  const diagnosticId = student?.student?.diagnostics?.[student?.student?.diagnostics?.length - 1]?.id ?? null;

  const timeColorClass = variant === 'too-long' ? 'tw-text-red-600' : 'tw-text-amber-500';

  return (
    <div
      className={`tw-flex tw-items-center tw-p-3 tw-border-b tw-border-solid ${
        isDismissed ? '!tw-bg-white' : ''
      } ${borderColorClass} tw-gap-2`}
    >
      <MaskedStudentData
        student={student.student}
        type={PiiTypes.FullName}
        className="tw-text-sm tw-font-atkinson tw-text-neutral-950 tw-flex-1 tw-min-w-0"
      >
        {studentName}
      </MaskedStudentData>
      <span className="tw-text-sm tw-font-atkinson tw-text-neutral-700 tw-w-20 tw-text-center">
        {variant === 'too-long' ? questionsAnswered : demonstratedSkills ?? '—'}
      </span>
      <span className={`tw-text-sm tw-font-bold tw-font-atkinson tw-w-16 tw-text-center ${timeColorClass}`}>
        {student.totalActiveTime == null ? '—' : formatActiveTime(student.totalActiveTime)}
      </span>
      {variant === 'too-long' && reserveActionsColumn && <span className="tw-w-44 tw-shrink-0" />}
      {variant === 'too-fast' && (
        <div className="tw-w-44 tw-shrink-0 tw-flex tw-items-center tw-justify-end tw-gap-2">
          {!isDismissed && (
            <>
              <Button link label="Dismiss" onClick={() => onDismiss?.(student)} />
              <Button
                className="tw-gap-x-2 tw-flex tw-flex-row-reverse"
                link
                label="Reset"
                icon={<RotateCw size={16} />}
                disabled={diagnosticId == null}
                onClick={() => toggleReset(true)}
              />
              <ResetDiagnosticDialog
                visible={showReset}
                onHide={() => toggleReset(false)}
                studentName={studentName}
                publicUserId={student.student.publicUserId}
                diagnosticId={diagnosticId as string}
                studentId={`StudentNode:${splitId(student.student.id)}`}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CloserLookStudentRow;
